/**
 * Org Y.Doc Store
 *
 * WS-only connection (no IndexedDB — the org room is re-seeded from MongoDB
 * on every cold start, which is incompatible with safe IDB caching).
 *
 * Room name: org:{dataspaceId}
 *
 * Y.Doc structure:
 *   Y.Map('edges') — edgeId → JSON { parentId, childId, pos, expanded, kind }
 *   Y.Map('nodes') — entityId → JSON { name, icon, entityType }
 *
 * Observers push changes into dataspaceTreeStore and the entity registry
 * (spaceEntityStore + globalEntityStore) so the sidebar updates in real time
 * when the backend (or another client) mutates tree structure or renames
 * entities.
 */

import * as Y from 'yjs'
import type { WebsocketProvider } from 'y-websocket'
import fetchService from '../services/fetchService'
import { MAIL_CRDT_TYPES } from './mailCrdtBridge'
import { entityRegistry } from './entityRegistry'
import { API_BASE_URL, WEBSOCKET_URL } from '../api/config'
import { wsClient, type WSClientHandle } from '../services/yjs/wsClientSingleton'
import { createFileLogger } from '../utils/logger'

const log = createFileLogger('ORG_YDOC_STORE', { enabled: false })

interface OrgYjsDebugState {
  dataspaceId: string | null
  wsConnected: boolean
  wsSynced: boolean
  wsUrl: string | null
  edgeCount: number
  nodeCount: number
  lastUpdateAt: number | null
}

let _debugState: OrgYjsDebugState = {
  dataspaceId: null,
  wsConnected: false,
  wsSynced: false,
  wsUrl: null,
  edgeCount: 0,
  nodeCount: 0,
  lastUpdateAt: null,
}

export function getOrgYDocDebugState(): OrgYjsDebugState {
  return { ..._debugState }
}

let _ydoc: Y.Doc | null = null
let _ws: WebsocketProvider | null = null
let _wsHandle: WSClientHandle | null = null
let _currentDataspaceId: string | null = null
let _readyResolvers: Array<{ resolve: () => void; reject: (err: Error) => void }> = []
let _isReady = false

type EntityChangeCallback = (action: 'add' | 'update' | 'delete', entityId: string, data?: any) => void
const _entityListeners = new Set<EntityChangeCallback>()

type ReconnectCallback = () => void
const _reconnectListeners = new Set<ReconnectCallback>()

export function subscribeToOrgReconnect(cb: ReconnectCallback): () => void {
  _reconnectListeners.add(cb)
  return () => _reconnectListeners.delete(cb)
}


function _notifyReadyWaiters(): void {
  _isReady = true
  const waiters = _readyResolvers.splice(0)
  waiters.forEach(({ resolve }) => resolve())
}

export function initOrgYDocStore(dataspaceId: string): void {
  if (!dataspaceId) return

  if (_currentDataspaceId === dataspaceId && _ws) {
    console.log(`[OrgYDoc] Already connected for dataspace ...${dataspaceId.slice(-6)}`)
    return
  }

  destroyOrgYDocStore()

  console.log(`[OrgYDoc] Initializing for dataspace ...${dataspaceId.slice(-6)}`)

  _currentDataspaceId = dataspaceId
  _isReady = false
  _debugState = {
    dataspaceId,
    wsConnected: false,
    wsSynced: false,
    wsUrl: WEBSOCKET_URL,
    edgeCount: 0,
    nodeCount: 0,
    lastUpdateAt: null,
  }

  _ydoc = new Y.Doc({ guid: `org:${dataspaceId}` })
  const edgesMap = _ydoc.getMap<string>('edges')
  const nodesMap = _ydoc.getMap<string>('nodes')
  const pendingCmdsMap = _ydoc.getMap<string>('pendingCmds')

  const roomName = `org:${dataspaceId}`
  // resyncInterval disabled: every periodic resync re-encodes and re-uploads the
  // FULL local Y.Doc state. For sessions where the doc has accumulated tombstones
  // / pendingCmds / many entity broadcasts, this state can grow past the server's
  // 64 MiB maxPayload, triggering [OVERFLOW] disconnects → circuit breaker → 5min
  // 503 storm that locks the user out of the entity tree. The org room is also
  // re-seeded from MongoDB on every cold start, so periodic resyncs provide
  // ~zero benefit for the cost. Live broadcasts still flow via edgesMap/nodesMap
  // observers; full reconciliation happens on next page load.
  _wsHandle = wsClient.open({
    roomname: roomName,
    doc: _ydoc,
    connect: true,
    resyncInterval: -1,
    maxBackoffTime: 30_000,
  })
  _ws = _wsHandle.provider

  // ── Overflow circuit-breaker detection ──────────────────────────────────────
  // Mirrors conversationYjsStore. The server sends close code 1008 when the
  // client's Y.Doc state exceeds 64 MiB. In practice the `ws` library terminates
  // the socket before the 1008 close frame can be delivered, so the client
  // usually sees 1006 (abnormal close) instead. Two patterns are detected:
  //   1008 + 'oversized'/'too large' reason → explicit overflow signal
  //   2 consecutive 1006 closes within 30s on a non-empty doc → implicit overflow
  //
  // Recovery: destroy + re-init the local Y.Doc (rather than reload the page).
  // A page reload loops if the server's circuit is still open (HTTP 503 → 1006
  // → reload → ∞). A targeted reset produces an empty Y.Doc which cannot
  // overflow; subsequent 1006s on the empty doc are ignored by the guard, so
  // y-websocket can back off until the server-side circuit window expires.
  let _consec1006Count = 0
  let _last1006At = 0
  let _recoveryInFlight = false
  const _capturedDataspaceId = dataspaceId

  const _triggerOverflowRecovery = () => {
    if (_recoveryInFlight) return
    _recoveryInFlight = true
    console.error(
      `[OrgYDoc] ⚠️ Payload overflow detected for ...${_capturedDataspaceId.slice(-6)} ` +
      `— destroying local Y.Doc and reconnecting fresh in 5s`
    )
    destroyOrgYDocStore()
    setTimeout(() => {
      if (_currentDataspaceId !== null && _currentDataspaceId !== _capturedDataspaceId) return
      initOrgYDocStore(_capturedDataspaceId)
    }, 5000)
  }

  const _attachOverflowListener = () => {
    if (!_ws || !_ws.ws) return
    _ws.ws.addEventListener('close', (event: CloseEvent) => {
      if (event.code === 1008 && /oversized|too large/i.test(event.reason || '')) {
        _triggerOverflowRecovery()
        return
      }
      if (event.code === 1006) {
        // Empty Y.Doc cannot overflow. 1006 on an empty doc is a circuit-rejection
        // (HTTP 503) or transient drop — don't treat it as overflow.
        const docIsEmpty = edgesMap.size === 0 && nodesMap.size === 0 && pendingCmdsMap.size === 0
        if (docIsEmpty) return
        const now = Date.now()
        if (now - _last1006At < 30_000) {
          _consec1006Count++
        } else {
          _consec1006Count = 1
        }
        _last1006At = now
        if (_consec1006Count >= 2) _triggerOverflowRecovery()
      }
    }, { once: true })
  }

  let _prevWsStatus: string | null = null
  _ws.on('status', ({ status }: { status: string }) => {
    _debugState.wsConnected = status === 'connected'
    console.log(`[OrgYDoc] WS ${status} for dataspace ...${dataspaceId.slice(-6)}`)
    // Re-attach the close listener whenever y-websocket creates a new socket
    // (the underlying ws is recreated on every reconnect).
    if (status === 'connecting') _attachOverflowListener()
    if (status === 'connected' && _prevWsStatus !== null && _prevWsStatus !== 'connected') {
      console.log(`[OrgYDoc] WS reconnected — notifying ${_reconnectListeners.size} listeners`)
      _reconnectListeners.forEach(fn => fn())
    }
    _prevWsStatus = status
  })

  _ws.on('sync', (synced: boolean) => {
    if (!synced) return
    _debugState.wsSynced = true
    _debugState.edgeCount = edgesMap.size
    _debugState.nodeCount = nodesMap.size
    console.log(`[OrgYDoc] WS synced: ${edgesMap.size} edges, ${nodesMap.size} nodes for ...${dataspaceId.slice(-6)}`)

    // Defer the heavy tree/node sync off the Yjs message handler via setTimeout so the
    // browser can service other events (input, paint) instead of blocking for ~1.7s.
    // Capture the dataspaceId at closure time so we can bail out if the store was
    // destroyed and re-initialized for a different dataspace before the timeout fires.
    const syncedDataspaceId = dataspaceId
    setTimeout(() => {
      if (_currentDataspaceId !== syncedDataspaceId) {
        console.log(`[OrgYDoc] Deferred sync for ...${syncedDataspaceId.slice(-6)} discarded — dataspace changed`)
        return
      }
      Promise.all([
        _applyFullTreeSync(edgesMap),
        _applyFullNodeSync(nodesMap),
      ]).then(() => {
        _notifyReadyWaiters()
      }).catch((err) => {
        console.warn('[OrgYDoc] Full sync apply failed:', err)
        _notifyReadyWaiters() // still unblock waiters so the app doesn't hang
      })
    }, 0)
  })

  edgesMap.observe((event: Y.YMapEvent<string>) => {
    if (!_debugState.wsSynced) return
    _debugState.edgeCount = edgesMap.size
    _debugState.lastUpdateAt = Date.now()
    _handleEdgeChanges(event, edgesMap)
  })

  nodesMap.observe((event: Y.YMapEvent<string>) => {
    if (!_debugState.wsSynced) return
    _debugState.nodeCount = nodesMap.size
    _debugState.lastUpdateAt = Date.now()
    _handleNodeChanges(event)
  })

  // Track command IDs already executed by this tab so that CRDT propagation delays
  // between tabs don't cause both tabs to execute the same non-idempotent command.
  const _executedCmdIds = new Set<string>()

  pendingCmdsMap.observe((event: Y.YMapEvent<string>) => {
    const now = Date.now()
    const toDelete: string[] = []
    event.changes.keys.forEach((change, id) => {
      if (change.action !== 'add') return
      // De-duplicate: skip if this tab already executed this command.
      if (_executedCmdIds.has(id)) return
      const raw = pendingCmdsMap.get(id)
      // Schedule deletion outside this observer to avoid re-entrant transactions.
      toDelete.push(id)
      if (!raw) return
      try {
        const { name, params, ts } = JSON.parse(raw)
        // Staleness window: tabs that reconnect after a short disconnect
        // should still execute recently-broadcast commands. Generic commands
        // time out at 5s; image-studio agent results are allowed up to 60s
        // because DALL-E runs are long and a tab opened mid-run should still
        // receive the completed result.
        const STALE_MS = name === 'image_studio_result' || name === 'image_studio_progress'
          ? 60_000
          : 5_000
        if (now - ts > STALE_MS) return  // stale — dropped
        _executedCmdIds.add(id)
        // Evict oldest half instead of clearing entirely — a full clear would forget
        // recently-executed IDs and allow re-execution of replayed CRDT events.
        if (_executedCmdIds.size > 500) {
          const toEvict = [..._executedCmdIds].slice(0, 250)
          toEvict.forEach(evictId => _executedCmdIds.delete(evictId))
        }
        console.log(`[OrgYDoc] pendingCmd: ${name}${params ? ` params=${JSON.stringify(params).slice(0, 120)}` : ''}`)

        // Infrastructure resync command — re-seeds the sidebar tree from the
        // org Y.Doc snapshot (with MongoDB fallback). Emitted by bulk ingest
        // after each ~500-message page so the browser sees new threads without
        // receiving per-entity CRDT floods.
        if (name === 'orgTreeResync') {
          const targetId = (params as any)?.dataspaceId || _currentDataspaceId
          if (targetId) {
            import('./dataspaceTreeStore').then(({ useDataspaceTreeStore }) => {
              useDataspaceTreeStore.getState().initialize(targetId)
            }).catch(() => {})
          }
          return
        }

        if (name === 'org_membership_revoked') {
          const p = params as any
          Promise.all([
            import('./userStore' as string),
            import('./dataspaceContextStore'),
            import('../services/ui/toastService'),
          ]).then(([userStoreMod, dsMod, toastMod]) => {
            const myUserId = (userStoreMod.default?.getState?.() as any)?.auth0Id
            if (!myUserId || myUserId !== p?.user_id) return
            const orgName = p?.org_name || 'an organization'
            toastMod.toastService.show(`You were removed from ${orgName}`, 'warning', { duration: 6000 })
            dsMod.useDataspaceContextStore.getState().switchToPersonal()
            window.location.href = '/home'
          }).catch(() => {})
          return
        }

        import('./commandRegistryStore').then(({ useCommandRegistryStore }) => {
          const registry = useCommandRegistryStore.getState()
          // Cross-tab command broadcasts are best-effort: another tab (or device) may be
          // running a newer/older app version that knows about commands this one doesn't.
          // Silently drop unknown commands rather than throwing, which otherwise surfaces
          // as a top-level React error.
          const isKnown = registry.commands.has(name as any) || registry.commandDefinitions.has(name as any)
          if (!isKnown) {
            console.debug(`[orgYDocStore] Ignoring cross-tab command unknown to this build: ${name}`)
            return
          }
          registry.executeCommand(name, params ?? {})
        }).catch(() => {})
      } catch {
        // malformed entry — ignore
      }
    })
    if (toDelete.length > 0) {
      queueMicrotask(() => {
        if (!_ydoc) return
        _ydoc.transact(() => {
          toDelete.forEach(id => pendingCmdsMap.delete(id))
        })
      })
    }
  })
}

export function destroyOrgYDocStore(): void {
  if (_wsHandle) {
    // wsHandle.close() handles tokenRefresher.unregister, observability dispose
    // and provider.disconnect+destroy with idempotent semantics.
    _wsHandle.close()
    _wsHandle = null
    _ws = null
  }
  if (_ydoc) {
    _ydoc.destroy()
    _ydoc = null
  }
  _currentDataspaceId = null
  _isReady = false
  // Reject pending waiters so they don't block for their full timeout.
  // Callers of waitForOrgSync / waitForEntitySystemReady handle rejection gracefully
  // via .catch(). Using reject (not resolve) prevents callers from incorrectly
  // assuming sync succeeded and proceeding with stale empty state.
  const pendingCount = _readyResolvers.length
  if (pendingCount > 0) {
    console.warn(`[OrgYDoc] Destroyed with ${pendingCount} pending waiters — rejecting them now`)
    const waiters = _readyResolvers.splice(0)
    waiters.forEach(({ reject }) => reject(new Error('[OrgYDoc] Destroyed before sync completed')))
  } else {
    _readyResolvers = []
  }
  _entityListeners.clear()
  if (_pendingFetchTimer !== null) {
    clearTimeout(_pendingFetchTimer)
    _pendingFetchTimer = null
  }
  _pendingFetchIds.splice(0)
  _pendingFetchScheduled = false
  // Clear the micro-batch edge flush state so stale edges from the old dataspace
  // are not applied to the next dataspace's tree store.
  _pendingEdgeAdds.clear()
  _pendingEdgeRemoves.clear()
  _edgeFlushScheduled = false
  _debugState = { dataspaceId: null, wsConnected: false, wsSynced: false, wsUrl: null, edgeCount: 0, nodeCount: 0, lastUpdateAt: null }
  console.log('[OrgYDoc] Destroyed')
}

export function waitForOrgSync(timeoutMs = 10000): Promise<void> {
  if (_isReady) {
    console.log(`[OrgYDoc] waitForOrgSync: already ready (fast path)`)
    return Promise.resolve()
  }
  console.log(`[OrgYDoc] waitForOrgSync: waiting (timeout=${timeoutMs}ms, pending=${_readyResolvers.length})`)
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`[OrgYDoc] WS sync timeout (${timeoutMs}ms)`))
    }, timeoutMs)
    _readyResolvers.push({
      resolve: () => { clearTimeout(timer); console.log('[OrgYDoc] waitForOrgSync: resolved'); resolve() },
      reject: (err) => { clearTimeout(timer); reject(err) },
    })
  })
}

export function writePendingCmdToOrgYDoc(name: string, params: Record<string, unknown> = {}): void {
  if (!_ydoc) return
  const pendingCmdsMap = _ydoc.getMap<string>('pendingCmds')
  const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  _ydoc.transact(() => {
    pendingCmdsMap.set(id, JSON.stringify({ name, params, ts: Date.now() }))
  })
  console.log(`[OrgYDoc] writePendingCmd: ${name}`)
}

/**
 * Write an edge to the org Y.Doc.
 * The Y.Map key is the semantic identity "(parentId or 'ROOT'):childId" — matching
 * getEdgeKey() on dataspaceTreeStore and the MongoDB unique index (dataspace_id,
 * child_id, parent_id). Two writers for the same logical edge overwrite the same slot.
 */
export function writeEdgeToOrgYDoc(data: {
  parentId: string | null
  childId: string
  pos: string
  expanded?: boolean
  kind?: 'canonical' | 'alias'
}): void {
  if (!_ydoc) return
  if (data.parentId !== null && data.parentId === data.childId) {
    console.error('[OrgYDoc] writeEdgeToOrgYDoc: refusing self-parent loop', data)
    return
  }
  const edgesMap = _ydoc.getMap<string>('edges')
  const edgeKey = (data.parentId || 'ROOT') + ':' + data.childId
  _ydoc.transact(() => {
    edgesMap.set(edgeKey, JSON.stringify({
      parentId: data.parentId,
      childId: data.childId,
      pos: data.pos,
      expanded: data.expanded ?? false,
      kind: data.kind ?? 'canonical',
    }))
  })
}

/**
 * Delete an edge from the org Y.Doc by its semantic identity.
 * The server-side observer records the deletion for the next writeState call.
 */
export function deleteEdgeFromOrgYDoc(childId: string, parentId: string | null): void {
  if (!_ydoc) return
  const edgesMap = _ydoc.getMap<string>('edges')
  const edgeKey = (parentId || 'ROOT') + ':' + childId
  _ydoc.transact(() => {
    edgesMap.delete(edgeKey)
  })
}

/**
 * Atomically move an edge in the org Y.Doc — delete old parent edge and write new
 * parent edge in a single Y.js transaction so they arrive at the server as one update.
 *
 * This is the safe version of calling deleteEdgeFromOrgYDoc + writeEdgeToOrgYDoc
 * for a move: both changes land in one edgesMap.observe() call on the server,
 * so _orgPendingDeletes and _orgPendingUpserts are updated together with no window
 * where the old key has been removed but the new key hasn't been recorded yet.
 *
 * When oldParentId === newParentId (same-parent reorder), only the write happens
 * (no delete needed).
 */
export function moveEdgeInOrgYDoc(data: {
  childId: string
  oldParentId: string | null
  newParentId: string | null
  pos: string
  expanded?: boolean
  kind?: 'canonical' | 'alias'
}): void {
  if (!_ydoc) return
  if (typeof data.childId !== 'string' || data.childId.length === 0) {
    console.error('[OrgYDoc] moveEdgeInOrgYDoc: invalid childId', data)
    return
  }
  if (data.newParentId !== null && data.newParentId === data.childId) {
    console.error('[OrgYDoc] moveEdgeInOrgYDoc: refusing self-parent loop', data)
    return
  }
  if (typeof data.pos !== 'string' || data.pos.length === 0) {
    console.error('[OrgYDoc] moveEdgeInOrgYDoc: invalid pos', data)
    return
  }
  const edgesMap = _ydoc.getMap<string>('edges')
  const oldKey = (data.oldParentId || 'ROOT') + ':' + data.childId
  const newKey = (data.newParentId || 'ROOT') + ':' + data.childId
  _ydoc.transact(() => {
    if (oldKey !== newKey) {
      edgesMap.delete(oldKey)
    }
    edgesMap.set(newKey, JSON.stringify({
      parentId: data.newParentId,
      childId: data.childId,
      pos: data.pos,
      expanded: data.expanded ?? false,
      kind: data.kind ?? 'canonical',
    }))
  })
}

export function writeNodeToOrgYDoc(entityId: string, data: {
  name: string
  icon: string | null
  entityType: string
}): void {
  if (!_ydoc) return
  const nodesMap = _ydoc.getMap<string>('nodes')
  _ydoc.transact(() => {
    nodesMap.set(entityId, JSON.stringify(data))
  })
}

/**
 * Delete a node from the local org Y.Doc, creating a Y.js tombstone.
 *
 * The tombstone propagates to the WS server on the next sync and from there
 * to all other connected clients, clearing any stale "running" Job entries that
 * survived reconnects because no delete was broadcast when the job completed.
 *
 * The `nodesMap.has()` guard is intentional: Y.js only creates a meaningful
 * tombstone when the key was previously set in THIS Y.Doc instance. Calling
 * delete on a key that was never set is a silent no-op with no propagation.
 */
export function deleteNodeFromOrgYDoc(entityId: string): void {
  if (!_ydoc) return
  const nodesMap = _ydoc.getMap<string>('nodes')
  if (!nodesMap.has(entityId)) return
  _ydoc.transact(() => {
    nodesMap.delete(entityId)
  })
}

export function subscribeToEntitySystemChanges(callback: EntityChangeCallback): () => void {
  _entityListeners.add(callback)
  return () => { _entityListeners.delete(callback) }
}

/**
 * Read all edges from the org Y.Doc as a snapshot.
 * Each entry's `edgeId` is the semantic key "(parentId or 'ROOT'):childId"
 * which equals getEdgeKey(childId, parentId) in dataspaceTreeStore.
 */
export function getOrgEdgesSnapshot(): Array<{
  edgeId: string
  parentId: string | null
  childId: string
  pos: string
  expanded: boolean
  kind: 'canonical' | 'alias'
}> {
  if (!_ydoc) return []
  const edgesMap = _ydoc.getMap<string>('edges')
  const result: ReturnType<typeof getOrgEdgesSnapshot> = []
  edgesMap.forEach((raw, edgeKey) => {
    try {
      const data = JSON.parse(raw)
      result.push({
        edgeId: edgeKey,  // semantic key — same format as nodesById keys
        parentId: data.parentId ?? null,
        childId: data.childId,
        pos: data.pos || 'M',
        expanded: data.expanded ?? false,
        kind: data.kind ?? 'canonical',
      })
    } catch { /* skip malformed */ }
  })
  return result
}

function _applyFullTreeSync(edgesMap: Y.Map<string>): Promise<void> {
  return import('./dataspaceTreeStore').then(({ useDataspaceTreeStore }) => {
    const store = useDataspaceTreeStore.getState()

    // If the tree hasn't been initialized yet, do a full replace from the Y.Doc.
    // If already initialized, merge — add/update edges without removing REST-sourced ones.
    if (!store.initialized) {
      const edges: Array<{
        id: string; dataspaceId: string; parentId: string | null; childId: string;
        pos: string; expanded: boolean; kind: 'canonical' | 'alias'; createdAt: string; updatedAt: string;
      }> = []

      edgesMap.forEach((raw, edgeKey) => {
        // edgeKey is the semantic key "(parentId or 'ROOT'):childId" after Phase 0a
        try {
          const data = JSON.parse(raw)
          edges.push({
            id: edgeKey,  // semantic key — used as nodesById key
            dataspaceId: _currentDataspaceId || '',
            parentId: data.parentId ?? null,
            childId: data.childId,
            pos: data.pos || 'M',
            expanded: data.expanded ?? false,
            kind: data.kind ?? 'canonical',
            createdAt: '',
            updatedAt: '',
          })
        } catch { /* skip malformed */ }
      })
      console.log(`[OrgYDoc] Full tree sync (init): ${edges.length} edges → initFromEdges`)
      store.initFromEdges(edges)
      return
    }

    // Merge: add or update edges from Y.Doc; don't remove edges not in Y.Doc.
    // edgeKey IS the nodesById key — no conversion needed.
    let merged = 0
    edgesMap.forEach((raw, edgeKey) => {
      try {
        const data = JSON.parse(raw)
        const existing = store.nodesById[edgeKey]
        if (!existing || existing.pos !== (data.pos || 'M') || existing.expanded !== (data.expanded ?? false)) {
          store._applyAddNode(edgeKey, {
            id: data.childId,
            parentId: data.parentId ?? null,
            pos: data.pos || 'M',
            expanded: data.expanded ?? false,
            kind: data.kind ?? 'canonical',
          })
          merged++
        }
      } catch { /* skip malformed */ }
    })
    if (merged > 0) {
      store._rebuildChildrenMap()
      console.log(`[OrgYDoc] Full tree sync (merge): ${merged} edges merged into existing tree`)
    }
  }).catch(err => {
    console.warn('[OrgYDoc] dataspaceTreeStore import failed:', err)
  })
}

function _applyFullNodeSync(nodesMap: Y.Map<string>): Promise<void> {
  // Walk the full nodes map and reconcile with whatever the registry already
  // holds. Only mutates entities the owning store already knows about — full
  // hydration of unknown entities flows through the per-space loader / global
  // store / app stores, not through this metadata sync.
  let updated = 0
  nodesMap.forEach((raw, entityId) => {
    try {
      const data = JSON.parse(raw)
      const existing = entityRegistry.getById(entityId)
      if (!existing) return
      const incomingName = data.name || existing.name
      const nameChanged = data.name && existing.name !== data.name
      const iconChanged = (existing as any).icon !== data.icon
      const existingSkeleton = (existing as any).skeleton
      const skeletonChanged = data.skeleton !== undefined && existingSkeleton !== undefined && (
        JSON.stringify(existingSkeleton) !== JSON.stringify({ ...existingSkeleton, ...data.skeleton })
      )
      if (nameChanged || iconChanged || skeletonChanged) {
        const merged: any = { ...existing, name: incomingName, icon: data.icon ?? (existing as any).icon }
        if (data.skeleton !== undefined) {
          merged.skeleton = { ...((existing as any).skeleton ?? {}), ...data.skeleton }
        }
        if (data.updated_at) merged.updated_at = data.updated_at
        entityRegistry.upsert(merged)
        updated++
      }
    } catch { /* skip */ }
  })
  if (updated > 0) {
    console.log(`[OrgYDoc] Full node sync: updated ${updated} entity names/skeletons`)
  }
  return Promise.resolve()
}

// Pending edge changes accumulated across multiple Yjs observer callbacks in the same tick.
// Flushed once per macrotask via setTimeout(0) so back-to-back Yjs messages only trigger
// a single _applyEdgeBatch (and therefore a single _rebuildChildrenMap).
type EdgeNode = { id: string; parentId: string | null; pos: string; expanded: boolean; kind: 'canonical' | 'alias' }
const _pendingEdgeAdds = new Map<string, EdgeNode>()
const _pendingEdgeRemoves = new Set<string>()
let _edgeFlushScheduled = false

function _scheduleEdgeFlush(): void {
  if (_edgeFlushScheduled) return
  _edgeFlushScheduled = true
  setTimeout(() => {
    _edgeFlushScheduled = false
    if (_pendingEdgeAdds.size === 0 && _pendingEdgeRemoves.size === 0) return
    const adds = Array.from(_pendingEdgeAdds.entries()) as [string, EdgeNode][]
    const removes = Array.from(_pendingEdgeRemoves)
    _pendingEdgeAdds.clear()
    _pendingEdgeRemoves.clear()
    import('./dataspaceTreeStore').then(({ useDataspaceTreeStore }) => {
      useDataspaceTreeStore.getState()._applyEdgeBatch(adds, removes)
    }).catch(err => {
      console.warn('[OrgYDoc] dataspaceTreeStore import failed:', err)
    })
  }, 0)
}

function _handleEdgeChanges(event: Y.YMapEvent<string>, edgesMap: Y.Map<string>): void {
  // Capture changes synchronously — event.changes is invalidated once the observer returns.
  for (const [edgeKey, change] of event.changes.keys) {
    if (change.action === 'add' || change.action === 'update') {
      const raw = edgesMap.get(edgeKey)
      if (!raw) continue
      try {
        const data = JSON.parse(raw)
        const parentId = data.parentId ?? null
        if (parentId !== null && parentId === data.childId) {
          console.error('[OrgYDoc] _handleEdgeChanges: dropping self-parent edge', edgeKey)
          continue
        }
        _pendingEdgeAdds.set(edgeKey, {
          id: data.childId,
          parentId,
          pos: data.pos || 'M',
          expanded: data.expanded ?? false,
          kind: data.kind ?? 'canonical',
        })
        _pendingEdgeRemoves.delete(edgeKey) // add wins over remove if both arrive
      } catch { /* skip malformed */ }
    } else if (change.action === 'delete') {
      _pendingEdgeRemoves.add(edgeKey)
      _pendingEdgeAdds.delete(edgeKey)
    }
  }

  _scheduleEdgeFlush()
}

function _emitEntityEvent(type: 'created' | 'updated' | 'deleted', entity: any): void {
  import('../services/entityEventService').then(({ entityEventService }) => {
    // Use emitEntityEvent directly so we can set source: 'crdt'. Editors
    // (NotesEditor, PageEditor, etc.) and requestCacheService need to see
    // these CRDT-originating changes; per-action subscribers ignore 'local'
    // events because they were already processed by the originating action.
    entityEventService.emitEntityEvent({
      type,
      entity,
      spaceId: entity.spaceId || _currentDataspaceId || '',
      source: 'crdt',
    })
  }).catch(() => {})
}

// Pending entity IDs that arrived via CRDT 'add' but aren't in the entity store.
// Collected within the current setTimeout window and flushed as a single batch request.
const _pendingFetchIds: string[] = []
let _pendingFetchScheduled = false
let _pendingFetchTimer: ReturnType<typeof setTimeout> | null = null

const BATCH_FETCH_LIMIT = 200

async function _executeBatchFetch(ids: string[], scheduledForDataspaceId: string | null): Promise<void> {
  // Split into chunks of BATCH_FETCH_LIMIT to stay within URL length limits.
  // Hydrated entities are dispatched through entityRegistry.upsert which routes
  // each one to its owning store (space/global/app) by entityType.
  //
  // Cross-org contamination guard: if the active dataspace changes while this
  // batch is mid-flight (org switch), drop the result on the floor. Otherwise
  // the registry would dispatch entities from the old org into the new org's
  // global/space caches, which both makes the UI lie and corrupts IDB.
  for (let i = 0; i < ids.length; i += BATCH_FETCH_LIMIT) {
    const chunk = ids.slice(i, i + BATCH_FETCH_LIMIT)
    const idsParam = chunk.map(encodeURIComponent).join(',')
    try {
      const result: any = await fetchService(`${API_BASE_URL}/entities?ids=${idsParam}`, { credentials: 'include' })
      if (_currentDataspaceId !== scheduledForDataspaceId) {
        console.log(`[OrgYDoc] Batch fetch result discarded — dataspace switched from ...${(scheduledForDataspaceId || '').slice(-6)} to ...${(_currentDataspaceId || '').slice(-6)}`)
        return
      }
      const entities: any[] = Array.isArray(result) ? result : (result?.entities ?? [])
      for (const entity of entities) {
        if (!entity?._id) continue
        // Mail entities are owned by mailAppStore — route via the dedicated bridge.
        if (entity.entityType && MAIL_CRDT_TYPES.has(entity.entityType)) {
          import('./mailCrdtBridge').then(({ handleMailCrdtUpdate }) => handleMailCrdtUpdate(entity)).catch(() => {})
          _emitEntityEvent('created', entity)
          continue
        }
        entityRegistry.upsert(entity)
        _emitEntityEvent('created', entity)
      }
      console.log(`[OrgYDoc] Batch fetch complete: ${entities.length}/${chunk.length} entities dispatched via registry`)
    } catch (err) {
      console.warn(`[OrgYDoc] Batch fetch failed for ${chunk.length} entities:`, err)
    }
  }
}

function _scheduleBatchFetch(): void {
  if (_pendingFetchScheduled) return
  _pendingFetchScheduled = true
  // Use setTimeout instead of queueMicrotask: each WebSocket message arrives as a
  // separate event loop task, so queueMicrotask only collects IDs within a single
  // message. A 50ms window collapses all entity-add events from a burst of WS
  // messages into one batch request. The timer ID is stored so destroyOrgYDocStore
  // can cancel it and prevent stale cross-session fetches.
  _pendingFetchTimer = setTimeout(() => {
    _pendingFetchTimer = null
    const ids = _pendingFetchIds.splice(0)
    _pendingFetchScheduled = false
    if (ids.length === 0) return

    // Capture the dataspace at scheduling time so the post-await dispatch
    // can bail if the user switched orgs while the request was in flight.
    const scheduledFor = _currentDataspaceId
    log.debug(`[OrgYDoc] Batch-fetching ${ids.length} new entities from REST (dataspace=...${(scheduledFor || '').slice(-6)})`)
    _executeBatchFetch(ids, scheduledFor).catch(() => {})
  }, 50)
}

function _handleNodeChanges(event: Y.YMapEvent<string>): void {
  // Capture changes synchronously while still inside the YJS observer callback.
  // event.changes is invalidated once the observer returns, so accessing it
  // inside an async .then() throws "You must not compute changes after the
  // event-handler fired."
  type NodeChange = { entityId: string; action: 'add' | 'update' | 'delete'; raw: string | null }
  const nodesMap = _ydoc?.getMap<string>('nodes')
  const captured: NodeChange[] = []

  for (const [entityId, change] of event.changes.keys) {
    if (change.action === 'add' || change.action === 'update') {
      captured.push({ entityId, action: change.action, raw: nodesMap?.get(entityId) ?? null })
    } else if (change.action === 'delete') {
      captured.push({ entityId, action: 'delete', raw: null })
    }
  }

  if (captured.length === 0) return

  for (const { entityId, action, raw } of captured) {
    if (action === 'add' || action === 'update') {
      if (!raw) continue
      try {
        const data = JSON.parse(raw)

        // Mail entities are not in the global entity store — route via the bridge.
        if (data.entityType && MAIL_CRDT_TYPES.has(data.entityType)) {
          import('./mailCrdtBridge').then(({ handleMailCrdtUpdate }) => handleMailCrdtUpdate(data)).catch(() => {})
          _entityListeners.forEach(fn => fn(action === 'add' ? 'add' : 'update', entityId, data))
          continue
        }

        const existing = entityRegistry.getById(entityId)
        if (existing) {
          // Skeleton-only broadcasts (e.g. entity_update writing inboxProcessing)
          // send name:"" which must not overwrite the real entity name. Only count
          // a name change when the incoming name is non-empty.
          const incomingName = data.name || existing.name
          const nameChanged = data.name && existing.name !== data.name
          const iconChanged = (existing as any).icon !== data.icon
          const skeletonChanged = data.skeleton !== undefined && (
            JSON.stringify((existing as any).skeleton) !== JSON.stringify({ ...((existing as any).skeleton ?? {}), ...data.skeleton })
          )
          if (nameChanged || iconChanged || skeletonChanged) {
            const merged: any = { ...existing, name: incomingName, icon: data.icon ?? (existing as any).icon }
            if (data.skeleton !== undefined) {
              merged.skeleton = { ...((existing as any).skeleton ?? {}), ...data.skeleton }
            }
            // Propagate updated_at from the broadcast so downstream consumers
            // (automationsStore fingerprint, AutomationForm) see the correct
            // timestamp. Cold-load seeds don't include updated_at; only overwrite
            // when the broadcast explicitly carries it.
            if (data.updated_at) merged.updated_at = data.updated_at
            entityRegistry.upsert(merged)
            _emitEntityEvent('updated', merged)
          }
        } else if (action === 'add') {
          // First time we're seeing this entity — enqueue a batch fetch so we
          // hydrate the full record via REST and dispatch through the registry.
          // We restrict this to `add` events: an `update` for an entity not in
          // any local store usually means the owning store hasn't mounted yet
          // (e.g. opening a space that hasn't been visited this session). The
          // store will hydrate on its own; fetching here would just duplicate
          // that work and burn a network round-trip.
          _pendingFetchIds.push(entityId)
          _scheduleBatchFetch()
        }
        _entityListeners.forEach(fn => fn(action === 'add' ? 'add' : 'update', entityId, data))
      } catch { /* skip malformed entry */ }
    } else if (action === 'delete') {
      // Mail bridge handles its own types; for everything else dispatch the
      // tombstone through the registry. The deleted node's type is no longer
      // readable from the Y.Map, so we let the registry fan out to all scopes.
      import('./mailCrdtBridge').then(({ handleMailCrdtDelete }) => handleMailCrdtDelete(entityId)).catch(() => {})
      const deletedEntity = entityRegistry.getById(entityId)
      entityRegistry.deleteEntity(entityId)
      if (deletedEntity) _emitEntityEvent('deleted', deletedEntity)
      _entityListeners.forEach(fn => fn('delete', entityId))
    }
  }
}
